import React from 'react'
import { useState, useEffect, useRef, useReducer, useContext } from 'react'
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom'
import {
    Button,
    Container,
    FormControl,
    TextField,
    FormControlLabel,
    h4,
    Radio,
    RadioGroup,
    Slide,
    Skeleton,
    Box,
    Alert,
    Tooltip,
    Card,
    CardContent,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Pagination,
    Chip,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import '../../App.css'
import Modal from '@mui/material/Modal'
import { useParams } from 'react-router-dom'
import { MultipleFileUploader } from '../../components/MultipleFileUploader'
import PDFWebViewComponent from '../../components/PDFWebViewComponent'

// Utils
import {
    uploadMultipleFilesUpdated,
    generateOutline,
    generateBoth,
    createINDModule2Section,
    extractAndStoreMultiple,
    createOutputVectorstore,
    processTemplate,
    listAllTemplates,
    listAllMRTTemplates,
    deleteTemplate,
    updateTemplateName,
    duplicateTemplate,
} from '../../utils/utils'
import { formatRelativeDate, getDateFromObjectId, formatCreatedDate } from '../../utils/dateUtils'

import { useAuthInfo } from '@propelauth/react'

// Custom Icons
import plus from '../../icons/Plus.svg'
import paperclip from '../../icons/Paper clip.svg'

// Contexts
import { useDemo } from '../../contexts/DemoContext'
import { useUser } from '../../contexts/UserContext'

// Custom Components
import CustomTable from '../../components/CustomTable'
import { CustomTableSkeleton } from '../../components/CustomTable'
import GeneratingTemplateModal from './steps/modals/GeneratingTemplateModal'
import OptionsMenu from '../../components/OptionsMenu'
import RenameFileModal from './modals/RenameFileModal'

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes in milliseconds
const CACHE_KEY_PREFIX = 'artos_template_cache_'

// Helper to estimate localStorage usage
const getLocalStorageSize = () => {
    let totalSize = 0
    for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
            totalSize += localStorage[key].length + key.length
        }
    }
    return totalSize
}

// Helper to limit data size before caching
const limitDataForCache = (data) => {
    const dataString = JSON.stringify(data)
    const maxSize = 1 * 1024 * 1024 // 1MB limit for caching
    
    if (dataString.length > maxSize) {
        console.warn(`Template data too large for cache (${(dataString.length / 1024 / 1024).toFixed(2)} MB), skipping cache...`)
        return null // Don't cache at all
    }
    return data
}

// Cache helper functions
const cacheHelpers = {
    getCacheKey: (orgId) => {
        return `${CACHE_KEY_PREFIX}${orgId}_templates`
    },
    
    getFromCache: (orgId) => {
        try {
            const cacheKey = cacheHelpers.getCacheKey(orgId)
            const cached = localStorage.getItem(cacheKey)
            
            if (!cached) return null
            
            const parsedCache = JSON.parse(cached)
            const now = Date.now()
            
            // Check if cache is expired
            if (now - parsedCache.timestamp > CACHE_TTL) {
                localStorage.removeItem(cacheKey)
                return null
            }
            
            return parsedCache.data
        } catch (error) {
            console.error('Error reading from cache:', error)
            return null
        }
    },
    
    saveToCache: (orgId, data) => {
        try {
            const limitedData = limitDataForCache(data)
            if (limitedData === null) {
                console.log('Skipping cache due to large data size')
                return // Don't cache
            }
            
            // Check localStorage size first
            const currentSize = getLocalStorageSize()
            const maxSize = 5 * 1024 * 1024 // 5MB
            
            if (currentSize > maxSize * 0.8) { // If over 80% full
                console.warn('localStorage is getting full, clearing old entries...')
                cacheHelpers.clearOldCacheEntries()
            }
            
            const cacheKey = cacheHelpers.getCacheKey(orgId)
            const cacheData = {
                data: limitedData,
                timestamp: Date.now()
            }
            
            // Try to save to cache
            try {
                localStorage.setItem(cacheKey, JSON.stringify(cacheData))
            } catch (error) {
                if (error.name === 'QuotaExceededError') {
                    console.warn('localStorage quota exceeded, clearing old entries...')
                    
                    // First, try to clear old cache entries
                    cacheHelpers.clearOldCacheEntries()
                    
                    // Try again
                    try {
                        localStorage.setItem(cacheKey, JSON.stringify(cacheData))
                    } catch (retryError) {
                        // If still failing, clear all template cache
                        console.warn('Still quota exceeded, clearing all template cache...')
                        cacheHelpers.clearAllCache()
                        
                        // Final attempt
                        try {
                            localStorage.setItem(cacheKey, JSON.stringify(cacheData))
                        } catch (finalError) {
                            // If still failing, clear ALL localStorage (nuclear option)
                            console.error('Cannot save to cache even after clearing. Clearing all localStorage...')
                            localStorage.clear()
                            // Don't try to save again to avoid infinite loop
                        }
                    }
                } else {
                    throw error
                }
            }
        } catch (error) {
            console.error('Error saving to cache:', error)
            // Don't throw - caching is not critical
        }
    },
    
    clearCache: (orgId) => {
        try {
            const cacheKey = cacheHelpers.getCacheKey(orgId)
            localStorage.removeItem(cacheKey)
        } catch (error) {
            console.error('Error clearing cache:', error)
        }
    },
    
    clearOldCacheEntries: () => {
        const now = Date.now()
        const keysToRemove = []
        
        // Define all known cache prefixes in the app
        const cachePrefixes = [
            CACHE_KEY_PREFIX,
            'artos_file_cache_', // From SourceDocumentsPage
            'artos_' // Any other artos-related cache
        ]
        
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i)
            if (key && cachePrefixes.some(prefix => key.startsWith(prefix))) {
                try {
                    const cached = JSON.parse(localStorage.getItem(key))
                    // Remove if expired OR if it's been more than 1 minute (more aggressive)
                    if (!cached.timestamp || now - cached.timestamp > 60000) {
                        keysToRemove.push(key)
                    }
                } catch (error) {
                    // If we can't parse it, it's probably corrupted - remove it
                    keysToRemove.push(key)
                }
            }
        }
        
        console.log(`Clearing ${keysToRemove.length} old cache entries...`)
        keysToRemove.forEach(key => {
            try {
                localStorage.removeItem(key)
            } catch (error) {
                console.error(`Failed to remove key ${key}:`, error)
            }
        })
    },
    
    clearAllCache: () => {
        const keysToRemove = []
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i)
            if (key && key.startsWith(CACHE_KEY_PREFIX)) {
                keysToRemove.push(key)
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key))
    }
}

// Utility function to diagnose and fix storage issues
const fixStorageIssues = () => {
    try {
        const size = getLocalStorageSize()
        console.log(`Current localStorage usage: ${(size / 1024).toFixed(2)}KB`)
        
        // Clear all old cache entries aggressively
        cacheHelpers.clearOldCacheEntries()
        
        // If still having issues, clear all Artos cache
        if (size > 4 * 1024 * 1024) { // Over 4MB
            console.warn('localStorage very full, clearing all Artos cache...')
            const keysToRemove = []
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i)
                if (key && key.startsWith('artos_')) {
                    keysToRemove.push(key)
                }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key))
        }
        
        const newSize = getLocalStorageSize()
        console.log(`New localStorage usage: ${(newSize / 1024).toFixed(2)}KB`)
        console.log(`Freed: ${((size - newSize) / 1024).toFixed(2)}KB`)
    } catch (error) {
        console.error('Error fixing storage issues:', error)
    }
}

const checkTemplateDataSize = (templateData) => {
    try {
        if (!templateData) {
            console.log('No template data provided')
            return { size: 0, readable: '0 bytes' }
        }

        const dataString = JSON.stringify(templateData)
        const sizeInBytes = dataString.length
        const sizeInKB = sizeInBytes / 1024
        const sizeInMB = sizeInKB / 1024

        let readable
        if (sizeInMB >= 1) {
            readable = `${sizeInMB.toFixed(2)} MB`
        } else if (sizeInKB >= 1) {
            readable = `${sizeInKB.toFixed(2)} KB`
        } else {
            readable = `${sizeInBytes} bytes`
        }

        console.log(`Template data size: ${readable}`)
        console.log(`Number of templates: ${Array.isArray(templateData) ? templateData.length : 'N/A (not array)'}`)
        
        if (Array.isArray(templateData) && templateData.length > 0) {
            const avgSize = sizeInBytes / templateData.length
            console.log(`Average size per template: ${(avgSize / 1024).toFixed(2)} KB`)
            
            // Check individual template sizes
            const largest = templateData
                .map((template, index) => ({
                    index,
                    name: template.template_name || template.source_document_name || 'Unknown',
                    size: JSON.stringify(template).length
                }))
                .sort((a, b) => b.size - a.size)
                .slice(0, 5) // Top 5 largest
            
            console.log('Top 5 largest templates:')
            largest.forEach(template => {
                console.log(`  ${template.name}: ${(template.size / 1024).toFixed(2)} KB`)
            })
        }

        return {
            size: sizeInBytes,
            readable,
            count: Array.isArray(templateData) ? templateData.length : 1
        }
    } catch (error) {
        console.error('Error checking template data size:', error)
        return { size: 0, readable: 'Error calculating size' }
    }
}

// Check current template cache size
const checkTemplateCacheSize = (orgId) => {
    try {
        const cacheKey = cacheHelpers.getCacheKey(orgId)
        const cached = localStorage.getItem(cacheKey)
        
        if (!cached) {
            console.log('No template cache found')
            return { size: 0, readable: '0 bytes' }
        }

        const sizeInBytes = cached.length
        const sizeInKB = sizeInBytes / 1024

        console.log(`Template cache size in localStorage: ${sizeInKB.toFixed(2)} KB`)
        
        // Also check the actual data
        try {
            const parsedCache = JSON.parse(cached)
            if (parsedCache.data) {
                console.log('Analyzing cached template data:')
                return checkTemplateDataSize(parsedCache.data)
            }
        } catch (parseError) {
            console.error('Error parsing cached data:', parseError)
        }

        return {
            size: sizeInBytes,
            readable: `${sizeInKB.toFixed(2)} KB`,
            count: 'Unknown'
        }
    } catch (error) {
        console.error('Error checking template cache size:', error)
        return { size: 0, readable: 'Error calculating size' }
    }
}

// Make it available globally for debugging
if (typeof window !== 'undefined') {
    window.fixArtosStorage = fixStorageIssues
    window.checkTemplateDataSize = checkTemplateDataSize
    window.checkTemplateCacheSize = checkTemplateCacheSize
}

function TemplatesPage() {
    const [templateFiles, setTemplateFiles] = useState([])
    const [newTemplateName, setNewTemplateName] = useState('')
    const [newTemplateModal, setNewTemplateModal] = useState(false)
    const [viewTemplateModal, setViewTemplateModal] = useState(false)
    const [fileUrl, setFileUrl] = useState('')
    const [showSuccessAlert, setShowSuccessAlert] = useState(false)
    const [successMessage, setSuccessMessage] = useState('Template successfully processed!')
    const [generatingTemplateModalOpen, setGeneratingTemplateModalOpen] = useState(false)
    const [step, setStep] = useState(0)
    const [status, setStatus] = useState('')

    // States for table-based UI
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [templateToDelete, setTemplateToDelete] = useState(null)
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false)
    const [templateToDuplicate, setTemplateToDuplicate] = useState(null)
    const [selectedTemplateToRename, setSelectedTemplateToRename] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [rowsPerPage] = useState(10)
    const [originalName, setOriginalName] = useState('')

    const { isDemo, setIsDemo } = useDemo()
    const { roles } = useUser()

    // Check if user is a Member (read-only access)
    const isMemberRole = roles.includes('Member')

    const documentTypeList = [
        'Nonclinical Overview',
        'Document Type 1',
        'Document Type 2',
        'Document Type 3',
        'Document Type 4',
        'Document Type 5',
    ]
    const [documentType, setDocumentType] = useState('')

    const [isRenameFileModalOpen, setIsRenameFileModalOpen] = useState(false)

    const [multipleFileList, setMultipleFileList] = useState([])
    const [multipleFileNameList, setMultipleFileNameList] = useState([])
    const [multipleFileContentList, setMultipleFileContentList] = useState([])

    const authInfo = useAuthInfo()

    const orgs = authInfo.orgHelper.getOrgs()

    const org = orgs[0].orgId

    const location = useLocation()
    const navigate = useNavigate()

    // Run storage fix on mount if needed
    useEffect(() => {
        try {
            // Try to write a test value to check if localStorage is working
            const testKey = 'artos_storage_test'
            localStorage.setItem(testKey, 'test')
            localStorage.removeItem(testKey)
        } catch (error) {
            if (error.name === 'QuotaExceededError') {
                console.warn('localStorage quota exceeded on mount, running storage fix...')
                fixStorageIssues()
            }
        }
    }, [])

    // Whether to display the created date column
    // remove this when created date is available in the backend
    const displayDate = false

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search)
        if (searchParams.get('success') === 'true') {
            setSuccessMessage('Template successfully created!')
            setShowSuccessAlert(true)
            // Remove the success parameter from URL after showing alert
            navigate('/templates', { replace: true })
            // Auto-hide the alert after 6 seconds
            setTimeout(() => setShowSuccessAlert(false), 6000)
        }
    }, [location, navigate])

    // useEffect(() => {
    //     console.log('templateFiles', templateFiles)
    //     if (didGenerateTemplate) {
    //         console.log('didGenerateTemplate', didGenerateTemplate)
    //         const lastPage = Math.ceil(templateFiles.length / rowsPerPage)
    //         setCurrentPage(lastPage)
    //         setDidGenerateTemplate(false)
    //     }
    // }, [templateFiles])

    async function getOrgAccessToken(orgId) {
        const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(orgId)
        // console.log('orgAccessToken', orgAccessToken)
        return orgAccessToken
    }

    // Status component with styling
    const StatusChip = ({ status }) => {
        let color, backgroundColor, label
        
        switch (status) {
            case 'Ready':
                color = '#059669'
                backgroundColor = '#D1FAE5'
                label = 'Generated'
                break
            case 'Generating':
                color = '#D97706'
                backgroundColor = '#FEF3C7'
                label = 'In Progress'
                break
            case 'N/A':
            default:
                color = '#6B7280'
                backgroundColor = '#F3F4F6'
                label = 'Not started'
                break
        }
        
        return (
            <Chip
                label={label}
                size="small"
                sx={{
                    color,
                    backgroundColor,
                    fontWeight: 500,
                    fontSize: '12px',
                    height: '20px',
                    borderRadius: '10px',
                    '& .MuiChip-label': {
                        padding: '0 8px'
                    }
                }}
            />
        )
    }

    // Process template data for table
    const templateRows = templateFiles.map((file, index) => {
        console.log('file', file)
        const templateName = file.template_name || file.template_nickname || file.source_document_name
        const referenceFile = file.source_document_name
        
        return {
            id: file.id,
            templateName,
            referenceFile,
            // lastUpdated: file.created_date || new Date().toLocaleString(),
            // lastUpdatedBy: 'Jane Doe', // Default value as requested
            status: file.status,
            document_type: file.detected_file_type,
            document_id: file.template_id,
            url: file.url,
            document_name: file.source_document_name,
            created: formatCreatedDate(file),
        }
    })

    // Calculate pagination
    // const startIndex = (currentPage - 1) * rowsPerPage
    // const endIndex = startIndex + rowsPerPage
    // const paginatedRows = templateRows.slice(startIndex, endIndex)
    // const totalPages = Math.ceil(templateRows.length / rowsPerPage)

    // Define table columns with proper proportions
    const tableColumns = [
        {
            field: 'templateName',
            header: 'Template name',
            width: '60%',
        },
        {
            field: 'status',
            header: 'Status',
            width: '20%',
            render: (value) => <StatusChip status={value} />
        },
        {
            field: 'created',
            header: 'Created',
            width: '20%',
        }
        // {
        //     field: 'lastUpdated',
        //     header: 'Last updated',
        //     width: '20%',
        // },
        // {
        //     field: 'lastUpdatedBy',
        //     header: 'Last updated by',
        //     width: '20%',
        // },
    ]

    const removeFile = (index) => {
        const newFileList = [...multipleFileList]
        newFileList.splice(index, 1)
        setMultipleFileList(newFileList) // Assuming your state setter is named like this
    }

    const handleContinue = () => {
        setStep((prevStep) => prevStep + 1)
    }

    const navigateToPageWithUrl = (params) => {
        navigate(`/${params}`)
    }

    const navigateToTemplate = (templateRow) => {
        if (templateRow.status === 'Ready') {
            const id = templateRow.id
            navigate(`/editTemplate/${id}`)
        } else {
            setGeneratingTemplateModalOpen(true)
            setStatus('warning')
        }
    }

    const handleGeneratingTemplateModalClose = () => {
        setGeneratingTemplateModalOpen(false)
        setStatus('')
    }

    // Handle menu open/close
    const handleMenuClick = (event, template) => {
        event.stopPropagation() // Prevent row click
        setAnchorEl(event.currentTarget)
        setSelectedTemplate(template)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
        // setSelectedTemplate(null)
    }

    // Handle delete
    const handleDeleteClick = () => {
        setTemplateToDelete(selectedTemplate)
        setDeleteDialogOpen(true)
        setSelectedTemplate(null)
        handleMenuClose()
    }

    const updateSelectedTemplateToRename = (selected) => {
        const updatedSelected = { ...selectedTemplateToRename, ...selected }
        setSelectedTemplateToRename(updatedSelected)
    }

    const clearSelectedTemplateToRename = () => {
        setSelectedTemplateToRename(null)
        setSelectedTemplate(null)
    }

    const handleSaveChanges = async () => {
        console.log('=== RENAME TEMPLATE: Save button clicked ===')
        const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
        const jsonPayload = {
            template_id: selectedTemplateToRename.document_id,
            old_name: originalName,
            new_name: selectedTemplateToRename.templateName
        }
        console.log('JSON Payload being sent to backend:', JSON.stringify(jsonPayload, null, 2))
        const result = await updateTemplateName(orgAccessToken, jsonPayload)
        console.log('Backend response:', result)

        // Refresh templates list from API and update cache
        console.log('Refreshing templates from API...')
        const refreshedTemplates = await listAllMRTTemplates(orgAccessToken)
        const filteredTemplateFiles = refreshedTemplates.filter(file => (file.template_name || file.source_document_name) || file.source_document_name)

        // Update state with new data
        setTemplateFiles(filteredTemplateFiles)

        // Update cache with fresh data
        cacheHelpers.saveToCache(org, refreshedTemplates)

        console.log('Templates list refreshed and cache updated')
        console.log('=== RENAME TEMPLATE: Complete ===')
    }

    const hasChangesTemplateToRename = () => {
        return JSON.stringify(selectedTemplateToRename) !== JSON.stringify(selectedTemplate)
    }

    const handleRenameClick = () => {
        console.log('handleRenameClick')
        console.log('selectedTemplate', selectedTemplate)
        setSelectedTemplateToRename(selectedTemplate)
        setOriginalName(selectedTemplate.templateName)
        setIsRenameFileModalOpen(true)
        console.log('selectedTemplateToRename', selectedTemplateToRename)
        handleMenuClose()
    }

    const handleRenameFileModalClose = () => {
        setIsRenameFileModalOpen(false)
    }

    const handleDeleteConfirm = async () => {
        // Placeholder delete function - just log/show message
        console.log('Delete template:', templateToDelete.templateName)
        const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
        const jsonPayload = {       
            document_nickname: templateToDelete.templateName
        }
        await deleteTemplate(orgAccessToken, jsonPayload)
        
        // Clear cache after deletion
        cacheHelpers.clearCache(org)
        
        navigate('/templates')
        // You could show a snackbar or alert here if needed
        setDeleteDialogOpen(false)
        setTemplateToDelete(null)
        
        // Reload templates after deletion
        setStatus('loading')
        try {
            const result = await listAllMRTTemplates(orgAccessToken)
            const filteredTemplateFiles = result.filter(file => (file.template_name || file.source_document_name) || file.source_document_name)
            setTemplateFiles(filteredTemplateFiles)
            
            // Save to cache
            cacheHelpers.saveToCache(org, result)
            
            setStatus('success')
            
            // Show success message for deletion
            setSuccessMessage('Template deleted successfully!')
            setShowSuccessAlert(true)
            setTimeout(() => setShowSuccessAlert(false), 3000)
        } catch (error) {
            console.error(error)
            setStatus('error')
        } finally {
            setStatus('')
        }
    }

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false)
        setTemplateToDelete(null)
        setSelectedTemplate(null)
    }

    // Add refresh handler
    const handleRefresh = async () => {
        // Clear cache and force reload
        cacheHelpers.clearCache(org)
        
        const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
        setStatus('loading')
        try {
            const result = await listAllMRTTemplates(orgAccessToken)
            const filteredTemplateFiles = result.filter(file => (file.template_name || file.source_document_name) || file.source_document_name)
            setTemplateFiles(filteredTemplateFiles)
            
            // Save to cache
            cacheHelpers.saveToCache(org, result)
            
            setStatus('success')
            setSuccessMessage('Templates refreshed successfully!')
            setShowSuccessAlert(true)
            setTimeout(() => setShowSuccessAlert(false), 3000)
        } catch (error) {
            console.error(error)
            setStatus('error')
        } finally {
            setStatus('')
        }
    }

    const handleDuplicateClick = () => {
        setTemplateToDuplicate(selectedTemplate)
        setDuplicateDialogOpen(true)
        setSelectedTemplate(null)
        handleMenuClose()
    }

    const handleDuplicateConfirm = async () => {
        const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
        setDuplicateDialogOpen(false)
        setStatus('loading')
        await duplicateTemplate(templateToDuplicate.document_id, orgAccessToken)
        
        // Clear cache after duplication
        cacheHelpers.clearCache(org)
        setTemplateToDuplicate(null)
        
        // Reload templates after duplication
        try {
            const result = await listAllMRTTemplates(orgAccessToken)
            const filteredTemplateFiles = result.filter(file => (file.template_name || file.source_document_name) || file.source_document_name)
            setTemplateFiles(filteredTemplateFiles)
            
            // Save to cache
            cacheHelpers.saveToCache(org, result)
            
            setStatus('success')
            
            // Show success message for duplication
            setSuccessMessage('Template duplicated successfully!')
            setShowSuccessAlert(true)
            setTimeout(() => setShowSuccessAlert(false), 3000)
        } catch (error) {
            console.error(error)
            setStatus('error')
        } finally {
            setStatus('')
        }
    }

    const handleDuplicateCancel = () => {
        setDuplicateDialogOpen(false)
        setTemplateToDuplicate(null)
        setSelectedTemplate(null)
    }

    useEffect(() => {
        async function retrieveInputs() {
            const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
            
            console.log('orgAccessToken', orgAccessToken)
            setStatus('loading')
            try {
                // Check if we're coming from successful template creation
                const searchParams = new URLSearchParams(location.search)
                const isFromSuccess = searchParams.get('success') === 'true'
                
                // Check cache first (unless we're coming from successful creation)
                if (!isFromSuccess) {
                    const cachedData = cacheHelpers.getFromCache(org)
                    if (cachedData) {
                        console.log('Using cached template data')
                        const filteredTemplateFiles = cachedData.filter(file => (file.template_name || file.source_document_name) || file.source_document_name)
                        console.log('filteredTemplateFiles', filteredTemplateFiles)
                        setTemplateFiles(filteredTemplateFiles)
                        setStatus('success')
                        return
                    }
                }

                // If no cache or expired, or coming from success, fetch from API
                console.log('Fetching templates from API')
                const result = await listAllMRTTemplates(orgAccessToken)
                console.log('result', result)
                const filteredTemplateFiles = result.filter(file => (file.template_name || file.source_document_name) || file.source_document_name)
                console.log('filteredTemplateFiles', filteredTemplateFiles)

                setTemplateFiles(filteredTemplateFiles) // Update state with fetched data
                
                // Add this line to check size of live data
                checkTemplateDataSize(filteredTemplateFiles)

                // Save to cache
                cacheHelpers.saveToCache(org, result)

                console.log('results', filteredTemplateFiles)

                setStatus('success') // Reset error state if successful
            } catch (error) {
                console.error(error)

                setStatus('error') // Set error state
            } finally {
                setStatus('') // End loading regardless of outcome
            }
        }
        retrieveInputs()
    }, [location.search]) // Add location.search as dependency

    const handleMultipleFiles = (files, fileContents) => {
        const newFiles = files.map((file, index) => ({
            name: file.name,
            content: fileContents[index],
        }))
        setMultipleFileList(newFiles)
        setMultipleFileNameList(newFiles.map((file) => file.name))
        setMultipleFileContentList(fileContents)
    }

    return (
        <Box sx={{ // Full width container
            width: '100%',
            margin: 0,
            padding: 0,
            boxSizing: 'border-box',
            height: '100%',
            overflowY: 'auto',
        }}>
            <Box sx={{ // Inner container
                width: '100%', 
                height: '100%', 
                padding: '40px 64px', 
                boxSizing: 'border-box',
                gap: '24px',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#F9FAFB',
            }}>
                <Box sx={{ // Header container
                    width: '100%',
                    margin: '0 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                }}>
                    {showSuccessAlert && (
                        <Alert
                            severity="success"
                            sx={{
                                mb: 2,
                                maxWidth: 'fit-content',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                            }}
                            onClose={() => setShowSuccessAlert(false)}
                        >
                            {successMessage}
                        </Alert>
                    )}
                    <Box sx={{ // Main panel header
                        width: '100%',
                        margin: '0 auto',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                    }}>
                        <Box sx={{ 
                            gap: '8px',
                            display: 'flex', 
                            flexDirection: 'column' 
                        }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}>
                                <Typography sx={{
                                    fontSize: '24px',
                                    fontWeight: 700,
                                    lineHeight: '36px',
                                    color: '#374151',
                                }}>
                                    Templates
                                </Typography>
                                <IconButton
                                    onClick={handleRefresh}
                                    disabled={status === 'loading'}
                                    title="Refresh templates"
                                    size="small"
                                    sx={{
                                        color: '#6B7280',
                                        '&:hover': {
                                            backgroundColor: '#F3F4F6',
                                        },
                                    }}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                                boxSizing: 'border-box',
                            }}>
                                <Typography sx={{
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    lineHeight: '20px',
                                    color: '#374151',
                                }}>
                                    Add your own word document templates to the Artos platform to create documents
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ 
                            gap: '8px',
                            display: 'flex',
                            paddingBottom: '8px',
                            alignItems: 'flex-start',
                        }}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => !isMemberRole && navigate('/createTemplate')}
                                disabled={isMemberRole}
                                sx={{
                                    backgroundColor: '#1F477D',
                                    color: 'white',
                                    height: '34px',
                                    width: '155px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    padding: '0 16px',
                                    '&:hover': {
                                        backgroundColor: '#2563EB',
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#9CA3AF',
                                        color: '#E5E7EB',
                                    },
                                    '& .MuiButton-startIcon': {
                                        marginRight: '8px',
                                        '& svg': {
                                            fontSize: '16px',
                                        },
                                    },
                                }}
                            >
                                Create template
                            </Button>
                        </Box>
                    </Box>
                </Box>

                <GeneratingTemplateModal
                    open={generatingTemplateModalOpen}
                    onClose={handleGeneratingTemplateModalClose}
                    loading={status === 'loading'}
                    success={status === 'success'}
                    error={status === 'error'}
                    errorMessage={''}
                    warning={status === 'warning'}
                    warningMessage={''}
                    onSuccess={() => {
                        navigate('/templates?success=true')
                    }}
                    onError={() => {
                        // Error handling is done in handleReviewConfirm
                    }}
                    // onWarning={() => {
                    //     // console.log("Status", status)
                    //     // Warning handling is done in handleReviewConfirm
                    //     // navigate('/templates')
                    //     // setGeneratingTemplateModalOpen(false)
                    // }}
                />

                {/* {!loading && error && <div>Error...</div>} */}
                
                {status === 'loading' ? (
                    <CustomTableSkeleton 
                        columns={tableColumns} 
                        backgroundColor={'#F9FAFB'} 
                    />
                ) : (
                    <Box sx={{ 
                        height: '100%',
                    }}>
                        <CustomTable
                            columns={tableColumns}
                            data={templateRows}
                            onRowClick={navigateToTemplate}
                            onMenuClick={handleMenuClick}
                            backgroundColor={'#F9FAFB'}
                        />
                    </Box>
                )}

                {/* Three-dot menu */}
                {/* <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                    <MenuItem onClick={handleDeleteClick} sx={{ color: '#DC2626' }}>
                        <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
                        Delete
                    </MenuItem>
                </Menu> */}
                <OptionsMenu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    onDelete={handleDeleteClick}
                    onRename={handleRenameClick}
                    onDuplicate={handleDuplicateClick}
                />

                {/* Delete confirmation dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={handleDeleteCancel}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Delete Template</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete "{templateToDelete?.templateName}"? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeleteCancel} color="inherit">
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Duplicate confirmation dialog */}
                <Dialog
                    open={duplicateDialogOpen}
                    onClose={handleDuplicateCancel}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Duplicate Template</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to duplicate "{templateToDuplicate?.templateName}"?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDuplicateCancel} color="inherit">
                            Cancel
                        </Button>
                        <Button onClick={handleDuplicateConfirm} color="primary" variant="contained">
                            Yes
                        </Button>
                    </DialogActions>
                </Dialog>

            </Box>
            <RenameFileModal
                open={isRenameFileModalOpen}
                onClose={handleRenameFileModalClose}
                modalTitle="Template"
                selected={selectedTemplateToRename}
                value="templateName"
                updateSelected={updateSelectedTemplateToRename}
                saveChanges={handleSaveChanges}
                clearSelected={clearSelectedTemplateToRename}
                hasChanges={hasChangesTemplateToRename}
            />
        </Box>
    )
}

export default TemplatesPage
